import { FC, useRef } from 'react';
import { OrderDoneButton } from './OrderDoneButton';
import {
  AllieIconButton,
  AllieItem,
  AllieList,
  AlliePopover,
  AllieText,
} from '../../../../components';
import { AllieEllipsisVerticalIcon, AllieLayersTwoIcon } from '../../../../styling/icons';
import { OrderProgressButtons } from './OrderProgressButtons';
import { OrderPauseButtons } from './OrderPauseButtons';
import { isTablet } from '../../../../common/utils';
import { PopoverContent, StyledDescText, ToolbarButtonWrapper } from '../ProductionOrder.styles';
import { ToolbarButtonsProps } from '../ProductionOrder.types';

export enum OrderStatus {
  Done = 'done',
  InProgress = 'in_progress',
  Pause = 'paused',
}

export const ToolbarButtons: FC<ToolbarButtonsProps> = ({
  orderId,
  status,
  onClickStartOp,
  playCircleOutline,
  isCurrentShift,
  showDrawerButton,
  onClickSeeAllPOs,
  disabledSeeAllPOs,
  onClickCloseOp,
  onClickPauseOp,
  onClickResumeOp,
  onClickEditOp,
  labels,
}) => {
  const popoverRef = useRef<HTMLIonPopoverElement>(null);
  const handleClickPopover = (cb: () => void) => {
    if (popoverRef.current) {
      popoverRef.current.dismiss();
    }
    cb();
  };

  const isModeAdminOP = onClickEditOp !== undefined;

  const mapToolbarByStatus = {
    [OrderStatus.Done]: (
      <OrderDoneButton
        onClickStartOp={onClickStartOp}
        playCircleOutline={playCircleOutline}
        isCurrentShift={isCurrentShift}
        btnStartPO={labels.btnStartPO}
      />
    ),
    [OrderStatus.InProgress]: (
      <OrderProgressButtons
        {...labels}
        onClickCloseOp={onClickCloseOp}
        onClickPauseOp={onClickPauseOp}
      />
    ),
    [OrderStatus.Pause]: (
      <OrderPauseButtons
        {...labels}
        onClickClose={onClickCloseOp}
        onClickStart={onClickStartOp}
        onClickResume={onClickResumeOp}
      />
    ),
  };

  return (
    <ToolbarButtonWrapper>
      {!!orderId ? (
        mapToolbarByStatus[status as keyof typeof mapToolbarByStatus]
      ) : (
        <OrderDoneButton
          onClickStartOp={onClickStartOp}
          playCircleOutline={playCircleOutline}
          isCurrentShift={isCurrentShift}
          {...labels}
        />
      )}
      {showDrawerButton && (
        <AllieIconButton
          fill="outline"
          allieType="secondary"
          allieColor="neutral"
          allieSize="small"
          corner="rounded-edges"
          className={`allie-text-button-normal production-order__action-button`}
          onClick={isModeAdminOP ? undefined : onClickSeeAllPOs}
          disabled={isModeAdminOP ? false : disabledSeeAllPOs}
          id="btn-actions-edit"
          style={{ flexGrow: isModeAdminOP ? 0 : 1 }}
        >
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {isModeAdminOP ? (
              <AllieEllipsisVerticalIcon style={{ width: 20, height: 20 }} />
            ) : (
              <AllieLayersTwoIcon style={{ width: 20, height: 20 }} />
            )}
            {!isModeAdminOP && isTablet && (
              <StyledDescText type="body3" weight={500}>
                {labels.seeOpButton}
              </StyledDescText>
            )}
          </div>
        </AllieIconButton>
      )}
      {onClickEditOp && (
        <AlliePopover
          ref={popoverRef}
          mode="ios"
          trigger="btn-actions-edit"
          triggerAction="click"
          alignment="end"
          content={{
            children: (
              <PopoverContent className="ion-content">
                <AllieList style={{ paddingLeft: 0 }}>
                  <AllieItem
                    onclick={() => handleClickPopover(onClickSeeAllPOs)}
                    detail={false}
                    button
                    disabled={disabledSeeAllPOs}
                  >
                    <AllieText
                      type="body3"
                      color="neutral-60"
                      weight={500}
                      style={{ marginLeft: 8 }}
                    >
                      {labels.seeOpButton}
                    </AllieText>
                  </AllieItem>
                  <AllieItem
                    onclick={() => handleClickPopover(onClickEditOp)}
                    detail={false}
                    button
                  >
                    <AllieText
                      type="body3"
                      color="neutral-60"
                      weight={500}
                      style={{ marginLeft: 8 }}
                    >
                      {labels.editOp}
                    </AllieText>
                  </AllieItem>
                </AllieList>
              </PopoverContent>
            ),
          }}
        />
      )}
    </ToolbarButtonWrapper>
  );
};
